"use client";

// Ported from relay-protocol packages/relay-widget/src/components/SourceForm.tsx
// (v0.1.0). Mechanical substitution only: CSS Modules `styles.x` references
// become literal "rd-x" class names (see ../styles/component.css's header
// mapping table), the CSS Modules import becomes the plain side-effect import
// below, viem's `formatUnits` is replaced by ../lib/units's self-implemented
// equivalent (dapp-ui may depend on neither wagmi nor viem), and the original
// `BalanceInfo` type (an inline shape re-exported by the source's
// useDepositAddress.ts) is expressed here as `RelayBalance & { formatted:
// string }` -- the ported ../hooks/useDepositAddress.ts's `balance` field is
// typed the same way rather than re-exporting a separate named type. One
// addition beyond the mechanical rename: `data-vaul-no-drag` on the amount and
// recipient <input>s, so a vaul drawer host doesn't mistake a text-selection
// drag inside either field for a drawer-dismiss swipe. Everything else --
// props, layout, and behavior -- is the original logic, untouched.
//
// Presentational: all state (catalog, selected origin, amount, recipient,
// balance) is passed in as props by <RelayDeposit>, which is the only
// component wired to useDepositAddress. The amount/recipient inputs are
// omitted entirely (`showAmountInput`/`showRecipientInput` false) when the
// host controls those values via RelayDeposit props -- see propsToOpts in
// RelayDeposit.tsx.
//
// Task 4 (address-first): amount is now entirely OPTIONAL -- the deposit
// address never waits on it -- so the label says so ("Amount (optional)")
// instead of implying it's required to proceed.
//
// 2026-07-30 redesign (approved step-1 "variant A"), presentation only -- no
// prop, handler or validation rule changed:
//   * the "1. Choose what you're sending" card title is gone (it duplicated
//     the new two-segment progress bar in the wizard header) and with it the
//     `.rd-card` wrapper -- the step body is the step, so it no longer nests a
//     surface inside the modal surface. `data-rd-source-form` replaces the
//     title as the identifying hook;
//   * chain + token became two half-width selectors on ONE row under a single
//     "From" label, each carrying a `subLabel` ("Network" / "N decimals") that
//     absorbs the old per-field label and the "chain 56 · 18 decimals" hint;
//   * the amount value is typeset large (22px/700, see `.rd-amount-input`) with
//     the MAX button inline after the symbol rather than floated over the
//     input, and the two hint paragraphs under it collapse into ONE meta line.
//
// 2026-07-31: the meta line carries the BALANCE only. The minimum moved into the
// checklist card under the address (`DepositChecklist.tsx`), which states the
// EFFECTIVE minimum -- the larger of the ops-set CDN floor and the catalog's own
// per-origin value -- so printing the catalog value here as well read as a
// second, smaller minimum. (The "your deposit address doesn't need an amount"
// sentence left earlier: the checklist's "reusable" line says it.)
//
// 2026-07-31 (user): the optional ONEchain RECIPIENT input left this file for
// <DepositPanel>'s "Deliver as" block. Fixed recipient values are no longer
// rendered there. What is left here is strictly the SOURCE: which chain, which
// token, and optionally how much.
import { useMemo } from "react";
import { formatUnits } from "../lib/units";
import type { Catalog, OriginOption } from "../lib/types";
import type { RelayBalance } from "../types";
import { ChainIcon, TokenIcon } from "./icons";
import { Dropdown } from "./Dropdown";
import { explorerTokenLink } from "../lib/explorer";
import "../styles/component.css";

export interface SourceFormProps {
  catalog: Catalog;
  origin?: OriginOption;
  onChangeOrigin: (origin: OriginOption) => void;
  amount: string;
  onChangeAmount: (value: string) => void;
  /** False when the host controls `amount` via a RelayDeposit prop -- hides the input entirely. */
  showAmountInput?: boolean;
  /** Connected wallet's balance of the selected origin token. */
  balance?: RelayBalance & { formatted: string };
  balanceLoading?: boolean;
  /** Set when the entered amount exceeds the wallet balance. */
  insufficientBalance?: boolean;
  /** When provided, renders a "Max" button that fills the full balance. */
  onMax?: () => void;
  disabled?: boolean;
}

/** One entry per distinct chain present in the catalog, in catalog order. */
function distinctChains(catalog: Catalog): { chainId: number; chainName: string }[] {
  const seen = new Map<number, string>();
  for (const origin of catalog.origins) {
    if (!seen.has(origin.chainId)) seen.set(origin.chainId, origin.chainName);
  }
  return Array.from(seen, ([chainId, chainName]) => ({ chainId, chainName }));
}

/** Every token offered on the given chain. */
function tokensForChain(catalog: Catalog, chainId: number | undefined): OriginOption[] {
  if (chainId === undefined) return [];
  return catalog.origins.filter((o) => o.chainId === chainId);
}

/** Formats a smallest-unit balance to a short human string (<=4 decimals).
 *
 * Truncates the already-exact `formatUnits` string rather than round-tripping it
 * through `Number` + `toLocaleString`: the latter rounds half-up (1.99999... ->
 * "2", i.e. MORE than the wallet holds) and silently loses precision above
 * 2^53, both of which CLAUDE.md §4 forbids for wei-scale values -- display is
 * floor, and a formatted string never re-enters arithmetic. */
function formatBalance(value: bigint, decimals: number): string {
  const s = formatUnits(value, decimals);
  const dot = s.indexOf(".");
  if (dot === -1) return s;
  const truncated = `${s.slice(0, dot)}.${s.slice(dot + 1, dot + 1 + 4)}`.replace(/\.?0+$/, "");
  return truncated || s.slice(0, dot);
}

export function SourceForm({
  catalog,
  origin,
  onChangeOrigin,
  amount,
  onChangeAmount,
  showAmountInput = true,
  balance,
  balanceLoading,
  insufficientBalance,
  onMax,
  disabled,
}: SourceFormProps) {
  const chains = useMemo(() => distinctChains(catalog), [catalog]);
  const tokenOptions = useMemo(() => tokensForChain(catalog, origin?.chainId), [catalog, origin?.chainId]);

  function handleChainChange(next: string) {
    const chainId = Number(next);
    const firstToken = tokensForChain(catalog, chainId)[0];
    if (firstToken) onChangeOrigin(firstToken);
  }

  function handleTokenChange(next: string) {
    const match = tokenOptions.find((o) => o.token.address.toLowerCase() === next.toLowerCase());
    if (match) onChangeOrigin(match);
  }

  const balanceDisplay = balance ? `${formatBalance(balance.value, balance.decimals)} ${balance.symbol}` : undefined;
  const amountError = insufficientBalance
    ? `Amount exceeds your balance${balanceDisplay ? ` (${balanceDisplay})` : ""}`
    : undefined;
  return (
    // `data-rd-source-form`: a stable hook for tests/hosts now that the
    // "1. Choose what you're sending" card title (the old identifying handle)
    // is gone -- the numbered titles duplicated the step progress bar.
    <section className="rd-stack" data-rd-source-form="">
      {/* Chain + token on ONE row as two half-width selectors, under a single
          "From" label: which network and which coin are one decision, and the
          brand marks identify them faster than two stacked labelled fields. */}
      <div className="rd-field">
        <div className="rd-field-label-row">
          <span className="rd-field-label">From</span>
          {origin &&
            (() => {
              const link = explorerTokenLink(origin.chainId, origin.token.address);
              if (!link) return null;
              return (
                <a
                  className="rd-token-explorer-link"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`View ${origin.token.symbol} on ${link.name}`}
                >
                  {link.name} ↗
                </a>
              );
            })()}
        </div>
        {/* The row's own children are the Dropdowns' `.rd-dropdown` roots --
            NOT <label>s: a <label> forwards clicks on non-interactive
            descendants (the listbox <li> options) to its first labelable
            control -- the dropdown trigger button -- so selecting an option
            would immediately re-toggle the popup open. Each Dropdown carries
            its own aria-label, so no accessibility is lost. */}
        <div className="rd-field-row">
          <Dropdown
            ariaLabel="Origin chain"
            value={origin ? String(origin.chainId) : ""}
            onChange={handleChainChange}
            disabled={disabled}
            options={chains.map((c) => ({
              value: String(c.chainId),
              label: c.chainName,
              subLabel: "Network",
              icon: <ChainIcon chainId={c.chainId} chainName={c.chainName} size={22} />,
            }))}
          />

          <Dropdown
            ariaLabel="Token"
            value={origin?.token.address ?? ""}
            onChange={handleTokenChange}
            disabled={disabled || tokenOptions.length === 0}
            options={tokenOptions.map((o) => ({
              value: o.token.address,
              label: o.token.symbol,
              // Replaces the old "chain 56 · 18 decimals" hint line under the
              // field: same fact, no extra row.
              subLabel: `${o.token.decimals} decimals`,
              icon: <TokenIcon symbol={o.token.symbol} logoUrl={o.token.logoUrl} size={22} />,
            }))}
          />
        </div>
      </div>

      {showAmountInput && (
        <label className="rd-field">
          <span className="rd-field-label">Amount (optional)</span>
          <div className="rd-amount-input" data-rd-invalid={amountError ? "" : undefined}>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={amount}
              onChange={(e) => {
                let next = e.target.value;
                if (next === "") {
                  onChangeAmount(next);
                  return;
                }
                // Keep the value a sensible decimal: digits + one dot, at most
                // 4 fraction digits (reject the keystroke otherwise), a leading
                // "." becomes "0.", redundant leading zeros collapse ("007" ->
                // "7"), and the integer part caps at 12 digits -- larger than
                // any real deposit, small enough to stop keyboard mashing.
                if (next.startsWith(".")) next = `0${next}`;
                if (!/^\d*\.?\d{0,4}$/.test(next)) return;
                next = next.replace(/^0+(?=\d)/, "");
                if ((next.split(".")[0] ?? "").length > 12) return;
                onChangeAmount(next);
              }}
              disabled={disabled || !origin}
              data-vaul-no-drag
            />
            {origin && <span className="rd-amount-input-suffix">{origin.token.symbol}</span>}
            {onMax && !disabled && (
              <button
                type="button"
                className="rd-amount-input-max"
                onClick={onMax}
                disabled={disabled || !origin}
              >
                Max
              </button>
            )}
          </div>
          {/* One meta line instead of the old two hint paragraphs. Balance only:
              the minimum is the checklist card's first line (see the header note
              above), and the "your deposit address doesn't need an amount"
              explainer is its "reusable" line. */}
          {(balanceDisplay || balanceLoading) && (
            <span className="rd-amount-meta">
              <span />
              <span>{balanceLoading ? "Balance: …" : balanceDisplay ? `Balance: ${balanceDisplay}` : ""}</span>
            </span>
          )}
          {amountError && <span className="rd-callout rd-callout-danger">{amountError}</span>}
        </label>
      )}

    </section>
  );
}
